import { useState, useEffect } from "react";
import { collection, query, where, orderBy, onSnapshot, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { converters } from "@/lib/firestore";
import type { EndUser } from "@/types/firestore";
import { ORG_END_USER_SLICE_LIMIT } from "@/lib/orgUserSlice";

export interface OrgEndUserWithId extends EndUser {
  id: string;
}

interface OrgEndUsersState {
  orgId: string | undefined;
  endUsers: OrgEndUserWithId[];
  loading: boolean;
  error: string | null;
  limitReached: boolean;
}

function initialState(orgId: string | undefined): OrgEndUsersState {
  return { orgId, endUsers: [], loading: !!orgId, error: null, limitReached: false };
}

/**
 * Loads the newest ORG_END_USER_SLICE_LIMIT endUsers for an org — a bounded
 * listener, not the full roster. `limitReached` flags that the slice hit its
 * cap; it does NOT prove a next user exists, so callers must not word it as a
 * verified omitted count. Any total/active count needs a separate aggregate
 * query (see `useOrgUserCounts`), never `endUsers.length` or a status filter
 * over this slice.
 *
 * State is tagged with the org it belongs to. React re-renders a component
 * with a new `orgId` prop *before* running effects, so without this tag the
 * hook would return the previous org's rows/counts for one render pass after
 * an org switch. The return statement masks state whose `orgId` doesn't
 * match the currently requested one, synchronously, during render — callers
 * never observe another org's data even transiently.
 */
export function useOrgEndUsers(orgId: string | undefined) {
  const [state, setState] = useState<OrgEndUsersState>(() => initialState(orgId));

  useEffect(() => {
    let cancelled = false;

    setState(initialState(orgId));

    if (!orgId) {
      return;
    }

    const ref = collection(db, "endUsers").withConverter(converters.endUsers);
    const q = query(
      ref,
      where("orgId", "==", orgId),
      orderBy("createdAt", "desc"),
      limit(ORG_END_USER_SLICE_LIMIT)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        // A callback from a listener this effect already tore down (e.g. a
        // late/racy delivery a mocked or misbehaving transport doesn't
        // suppress) must not resurrect a stale org's rows.
        if (cancelled) return;
        setState({
          orgId,
          endUsers: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          loading: false,
          error: null,
          limitReached: snap.docs.length >= ORG_END_USER_SLICE_LIMIT,
        });
      },
      (err) => {
        if (cancelled) return;
        console.error("Error fetching org end users:", err);
        setState({ orgId, endUsers: [], loading: false, error: "Failed to load users.", limitReached: false });
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [orgId]);

  const isCurrent = state.orgId === orgId;
  return {
    endUsers: isCurrent ? state.endUsers : [],
    loading: isCurrent ? state.loading : !!orgId,
    error: isCurrent ? state.error : null,
    limitReached: isCurrent ? state.limitReached : false,
  };
}
